import React, { lazy, Suspense, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { AdminSystemV2Page } from './adminSystem/AdminSystemV2Page';
import { V2_TO_LEGACY } from './adminSystem/legacyTabs';
import {
  Settings as SettingsIcon,
  Build as DataRepairIcon,
  Transform as NormalizationIcon,
  Shield as PrivacyIcon,
  Flag as FeatureFlagsIcon,
  RocketLaunch as ReleaseStepsIcon,
} from '@mui/icons-material';
import { PageHeader, TabShell, TabShellTab } from '../components/ui';
import { useUrlTab } from '../hooks/useUrlTab';
import { useAppTranslation } from '../i18n/useAppTranslation';

// Lazy load the tab content components for better performance
const AdminSystemSettingsPage = lazy(() =>
  import('./AdminSystemSettingsPage').then(module => ({ default: module.AdminSystemSettingsPage }))
);
const AdminDataRepairPage = lazy(() => import('./AdminDataRepairPage'));
const AdminProductNormalizationPage = lazy(() => import('./AdminProductNormalizationPage'));
const AdminPrivacyRequestsPage = lazy(() => import('./AdminPrivacyRequestsPage'));
const AdminFeatureFlagsPage = lazy(() => import('./AdminFeatureFlagsPage'));
const AdminReleaseStepsPage = lazy(() => import('./AdminReleaseStepsPage'));

// One flat row of tabs: General settings sit beside the other System tools,
// and Feature Flags (which holds every on/off switch) is its own tab.
type SystemTabKey =
  | 'general'
  | 'data-repair'
  | 'normalization'
  | 'privacy-requests'
  | 'feature-flags'
  | 'release-steps';

const TAB_KEYS: readonly SystemTabKey[] = [
  'general',
  'data-repair',
  'normalization',
  'privacy-requests',
  'feature-flags',
  'release-steps',
];

// Loading fallback component
const TabLoadingFallback: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, py: 8 }}>
    <CircularProgress />
  </Box>
);

const LegacySystemPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('tab');
  useEffect(() => {
    const mapped = raw ? V2_TO_LEGACY[raw] : undefined;
    if (!mapped) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (mapped === 'general') next.delete('tab');
      else next.set('tab', mapped);
      return next;
    }, { replace: true });
  }, [raw, setSearchParams]);
  const [activeTab, setActiveTab] = useUrlTab(TAB_KEYS, 'general');
  const { t } = useAppTranslation();

  // Tab configuration
  const tabs: TabShellTab<SystemTabKey>[] = [
    { key: 'general', label: 'General', icon: <SettingsIcon /> },
    { key: 'data-repair', label: 'Data Repair', icon: <DataRepairIcon /> },
    { key: 'normalization', label: 'Normalization', icon: <NormalizationIcon /> },
    { key: 'privacy-requests', label: 'Privacy Requests', icon: <PrivacyIcon /> },
    { key: 'feature-flags', label: t('admin.featureFlags.title'), icon: <FeatureFlagsIcon /> },
    { key: 'release-steps', label: 'Release steps', icon: <ReleaseStepsIcon /> },
  ];

  return (
    <Box sx={{ minHeight: '100%' }}>
      <Box sx={{ px: 3, pt: 2 }}>
        <PageHeader
          title="System"
          description="Platform settings, data repair, normalization, privacy requests, feature flags and per-stack release steps in one place."
        />
      </Box>

      <TabShell tabs={tabs} value={activeTab} onChange={setActiveTab} aria-label="System sections">
        <Suspense fallback={<TabLoadingFallback />}>
          {activeTab === 'general' && <AdminSystemSettingsPage />}
          {activeTab === 'data-repair' && <AdminDataRepairPage />}
          {activeTab === 'normalization' && <AdminProductNormalizationPage />}
          {activeTab === 'privacy-requests' && <AdminPrivacyRequestsPage />}
          {activeTab === 'feature-flags' && <AdminFeatureFlagsPage />}
          {activeTab === 'release-steps' && <AdminReleaseStepsPage />}
        </Suspense>
      </TabShell>
    </Box>
  );
};

export const AdminSystemPage: React.FC = () => {
  const { enabled, isLoading } = useOrgFeatureFlag('admin_system_v2');
  if (isLoading) return <TabLoadingFallback />;
  if (enabled) return <AdminSystemV2Page />;
  return <LegacySystemPage />;
};

export default AdminSystemPage;
